import 'keen-slider/keen-slider.min.css'
import React, { useEffect, useState, useCallback } from 'react'
import { useKeenSlider } from 'keen-slider/react' // import from 'keen-slider/react.es' for to get an ES module
import { useTheme, css } from '@emotion/react'
import { BsCircle } from 'react-icons/bs'

function Carousel({
    children,
    rtl = false,
    loop = true,
    preview = { sm: 1, md: 2, lg: 4, xl: 6, xxl: 7 },
    controls = true,
    dots = false,
    vertical = false,
    autoSwitch = false,
    switchTime = 5000,
    style,
    icon = <BsCircle />,
    className,
}) {
    const autoplay = useCallback((slider) => {
        if (!slider) return
        let timeout
        let mouseOver = false
        function clearNextTimeout() {
            clearTimeout(timeout)
        }
        function nextTimeout() {
            clearTimeout(timeout)
            if (mouseOver) return
            timeout = setTimeout(() => {
                slider?.next()
            }, switchTime)
        }
        slider.on('created', () => {
            slider.container.addEventListener('mouseover', () => {
                mouseOver = true
                clearNextTimeout()
            })
            slider.container.addEventListener('mouseout', () => {
                mouseOver = false
                nextTimeout()
            })
            nextTimeout()
        })
        slider.on('dragStarted', clearNextTimeout)
        slider.on('animationEnded', nextTimeout)
        slider.on('updated', nextTimeout)
    }, [switchTime])
    const plugins = autoSwitch ? autoplay : () => { }
    const [currentSlide, setCurrentSlide] = React.useState(0)
    const [loaded, setLoaded] = useState(false)
    const [refCallback, instanceRef] = useKeenSlider(
        {
            slideChanged(slider) {
                setCurrentSlide(slider.track.details.rel)
            },
            created() {
                setLoaded(true)
            },
            rtl: rtl,
            vertical: vertical,
            loop: loop,
            breakpoints: {
                '(min-width: 640px)': {
                    slides: { perView: preview.md, spacing: 5 },
                },
                '(min-width: 1024px)': {
                    slides: { perView: preview.lg, spacing: 15 },
                },
                '(min-width: 1280px)': {
                    slides: { perView: preview.xl, spacing: 15 },
                },
                '(min-width: 1580px)': {
                    slides: { perView: preview.xxl, spacing: 15 },
                },
            },
            slides: {
                perView: preview.sm,
            },
        },
        [
            // add plugins here
            plugins,
        ]
    )

    useEffect(() => {
        instanceRef?.current?.update()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children])

    return (
        <div
            css={css`
                position: relative;
                ${style}
            `}
        >
            <div
                css={css`
                    position: relative;
                `}
                className={className}
            >
                <div ref={refCallback} className="keen-slider">
                    {children}
                </div>
                {controls && loaded && instanceRef.current && (
                    <>
                        <Arrow
                            left
                            onClick={(e) =>
                                e.stopPropagation() ||
                                instanceRef.current?.next()
                            }
                            disabled={currentSlide === 0}
                            arrowStyle="left:0; top:0; margin:auto; height:100%; cursor:pointer; position:absolute; background: linear-gradient(to left, transparent, 10%, white); border-right: 1px solid #ffffff;"
                        />

                        <Arrow
                            onClick={(e) =>
                                e.stopPropagation() ||
                                instanceRef.current?.prev()
                            }
                            disabled={
                                currentSlide ===
                                instanceRef.current.track.details?.slides
                                    .length -
                                1
                            }
                            arrowStyle="right:0; top:0; margin:auto; height:100%; cursor:pointer; position:absolute; background: linear-gradient(to right, transparent ,10%, white); border-left: 1px solid #ffffff;"
                        />
                    </>
                )}
            </div>
            {dots && loaded && instanceRef.current && (
                <div className="dots">
                    {[
                        ...Array(
                            instanceRef.current.track?.details?.slides
                                ?.length ?? 0
                        ).keys(),
                    ].map((idx) => {
                        return (
                            <button
                                key={idx}
                                onClick={() => {
                                    instanceRef.current?.moveToIdx(idx)
                                }}
                                className={
                                    'dot' +
                                    (currentSlide === idx ? ' active' : '')
                                }
                            >
                                {icon}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

function Arrow(props) {
    const disabeld = props.disabled ? ' arrow--disabled' : ''
    const theme = useTheme()
    return (
        <svg
            onClick={props.onClick}
            className={`arrow w-6 ${props.className} ${props.left ? 'arrow--left' : 'arrow--right'
                } ${disabeld}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={theme.colors?.secGray}
            css={css`
                ${props.arrowStyle}
            `}
        >
            {props.left && (
                <path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
            )}
            {!props.left && (
                <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
            )}
        </svg>
    )
}

export default Carousel
